import React, { useEffect, useState } from "react";
import "./AutoPopupAdmin.css";

const API_BASE = process.env.REACT_APP_API_BASE ?? (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");
const emptyForm = { title: "", message: "", media_path: "", media_type: "", start_at: "", end_at: "", enabled: true };
const token = () => localStorage.getItem("token") || "";
const localDateValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export default function AutoPopupAdmin() {
    const [form, setForm] = useState(emptyForm);
    const [rows, setRows] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");

    const loadRows = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/auto-popups`, { headers: { Authorization: `Bearer ${token()}` } });
            const data = await response.json();
            if (!response.ok || !data.ok) throw new Error(data.error || "Unable to load scheduled popups.");
            setRows(data.rows || []);
        } catch (error) { setMessage(error.message); }
    };

    useEffect(() => { loadRows(); }, []);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true); setMessage("");
        try {
            const body = new FormData(); body.append("file", file);
            const response = await fetch(`${API_BASE}/api/auto-popups/upload`, { method: "POST", headers: { Authorization: `Bearer ${token()}` }, body });
            const data = await response.json();
            if (!response.ok || !data.ok) throw new Error(data.error || "Upload failed.");
            setForm((current) => ({ ...current, media_path: data.path, media_type: data.mediaType }));
            setMessage("Media uploaded. Complete the schedule and save.");
        } catch (error) { setMessage(error.message); }
        finally { setUploading(false); }
    };

    const useHolidayVideo = () => {
        setForm((current) => ({ ...current, media_path: "/videos/SARCXMas.mp4", media_type: "video" }));
        setMessage("Existing SARC holiday video selected.");
    };

    const save = async (event) => {
        event.preventDefault(); setBusy(true); setMessage("");
        try {
            const payload = {
                ...form,
                start_at: form.start_at ? new Date(form.start_at).toISOString() : "",
                end_at: form.end_at ? new Date(form.end_at).toISOString() : "",
            };
            const response = await fetch(`${API_BASE}/api/auto-popups${editingId ? `/${editingId}` : ""}`, {
                method: editingId ? "PUT" : "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!response.ok || !data.ok) throw new Error(data.error || "Unable to save popup.");
            setForm(emptyForm); setEditingId(null); setMessage(editingId ? "Scheduled popup updated." : "Scheduled popup created.");
            await loadRows();
        } catch (error) { setMessage(error.message); }
        finally { setBusy(false); }
    };

    const edit = (row) => {
        setEditingId(row.id);
        setForm({ title: row.title, message: row.message || "", media_path: row.media_path, media_type: row.media_type, start_at: localDateValue(row.start_at), end_at: localDateValue(row.end_at), enabled: row.enabled });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete scheduled popup “${row.title}”?`)) return;
        try {
            const response = await fetch(`${API_BASE}/api/auto-popups/${row.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token()}` } });
            const data = await response.json();
            if (!response.ok || !data.ok) throw new Error(data.error || "Delete failed.");
            setMessage("Scheduled popup deleted."); await loadRows();
        } catch (error) { setMessage(error.message); }
    };

    const previewUrl = form.media_path ? (form.media_path.startsWith("/videos/") ? form.media_path : `${API_BASE}${form.media_path}`) : "";
    const status = (row) => {
        if (!row.enabled) return "Disabled";
        const now = Date.now(), start = new Date(row.start_at).getTime(), end = new Date(row.end_at).getTime();
        return now < start ? "Scheduled" : now < end ? "Live" : "Ended";
    };

    return <div className="auto-popup-admin">
        <h3>{editingId ? "Edit Auto Popup" : "Create Auto Popup"}</h3>
        <p className="auto-popup-help">Schedule one image, GIF, or video to open automatically on the public home page.</p>
        {message && <div className="auto-popup-message">{message}</div>}
        <form onSubmit={save} className="auto-popup-form">
            <label>Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></label>
            <label>Message<textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></label>
            <div className="auto-popup-dates"><label>Show From<input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} required /></label><label>Hide On<input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} required /></label></div>
            <label>Upload Video, GIF, or Image<input type="file" accept="image/*,video/*" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} /></label>
            <div className="auto-popup-media-actions"><button type="button" onClick={useHolidayVideo}>Use SARCXMas.mp4</button>{form.media_path && <button type="button" onClick={() => setForm({ ...form, media_path: "", media_type: "" })}>Clear Media</button>}</div>
            <label className="auto-popup-enabled"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled</label>
            <div className="auto-popup-preview">{previewUrl ? form.media_type === "video" ? <video src={previewUrl} controls muted /> : <img src={previewUrl} alt="Popup preview" /> : <em>No media selected</em>}</div>
            <div className="auto-popup-actions"><button className="btn-primary" disabled={busy || uploading}>{busy ? "Saving…" : editingId ? "Update Popup" : "Save Popup"}</button>{editingId && <button type="button" className="btn-secondary" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancel</button>}</div>
        </form>
        <h4>Scheduled Auto Popups</h4>
        <div className="table-wrap auto-popup-table"><table className="nice-table light-table"><thead><tr><th>Title</th><th>Media</th><th>Show From</th><th>Hide On</th><th>Status</th><th>Actions</th></tr></thead><tbody>{rows.length ? rows.map((row) => <tr key={row.id}><td>{row.title}</td><td>{row.media_type}</td><td>{new Date(row.start_at).toLocaleString()}</td><td>{new Date(row.end_at).toLocaleString()}</td><td><span className={`auto-popup-status ${status(row).toLowerCase()}`}>{status(row)}</span></td><td><button type="button" onClick={() => edit(row)}>Edit</button> <button type="button" className="danger" onClick={() => remove(row)}>Delete</button></td></tr>) : <tr><td colSpan="6">No scheduled popups.</td></tr>}</tbody></table></div>
    </div>;
}
